import type { EchoflicksVideoSession } from "../types";
import {
  filmRating,
  studioCreator,
  studioCreatorLabel,
  type LoginLookup,
  type StudioCreator,
} from "./studioFilms";

export { filmRating };

/**
 * Agent ratings of Studio films (plan 2026-10-10). An agent rates a finished
 * film 1–5 on real-estate.echoflicks.com; the bot sends the WhatsApp rating's own
 * `feedback.received` event, so the score and what they said land on the film's
 * session row as `rating` / `feedback_text` / `feedback_at`, like any rating.
 * These helpers read them for the Agencies page.
 */

type RatedFilmRow = Pick<
  EchoflicksVideoSession,
  | "id"
  | "rating"
  | "feedback_text"
  | "feedback_at"
  | "movie_title"
  | "script_title"
  | "studio_created_by"
  | "studio_created_by_staff"
>;

export type StudioFilmRating = {
  sessionId: string;
  title: string;
  rating: number;
  feedbackText: string | null;
  feedbackAt: string | null;
  creator: StudioCreator & { label: string };
};

/** The newest rated films first (by when they were rated), at most `limit`. */
export function latestStudioRatings(
  films: readonly RatedFilmRow[],
  logins: LoginLookup | null | undefined,
  limit = 10,
): StudioFilmRating[] {
  return films
    .flatMap((film) => {
      const rating = filmRating(film.rating);
      if (rating == null) return [];
      const creator = studioCreator(film, logins);
      const text =
        typeof film.feedback_text === "string" && film.feedback_text.trim()
          ? film.feedback_text.trim()
          : null;
      return [
        {
          sessionId: String(film.id),
          title:
            film.movie_title?.trim() ||
            film.script_title?.trim() ||
            "Untitled film",
          rating,
          feedbackText: text,
          feedbackAt: film.feedback_at ?? null,
          creator: { ...creator, label: studioCreatorLabel(creator) },
        },
      ];
    })
    .sort((a, b) =>
      String(b.feedbackAt ?? "").localeCompare(String(a.feedbackAt ?? "")),
    )
    .slice(0, Math.max(0, limit));
}

/** How many films carry a rating, and their mean (one decimal), or null. */
export function ratingSummary(films: readonly Pick<RatedFilmRow, "rating">[]): {
  rated: number;
  average: number | null;
} {
  const scores = films
    .map((film) => filmRating(film.rating))
    .filter((rating): rating is number => rating != null);
  if (!scores.length) return { rated: 0, average: null };
  const mean = scores.reduce((sum, rating) => sum + rating, 0) / scores.length;
  return { rated: scores.length, average: Math.round(mean * 10) / 10 };
}

/** "★★★★☆" for a whole score (half points round down). */
export const ratingStars = (rating: number): string => {
  const whole = Math.max(0, Math.min(5, Math.floor(rating)));
  return "★".repeat(whole) + "☆".repeat(5 - whole);
};
